<template>
  <set-card id="sim" :title="$t('settings_shell.sim.title')" :desc="$t('settings_shell.sim.desc')" tone="sim">
    <div class="ss-sim">
      <div class="ss-sim__ctl">
        <div class="ss-dchips" role="group" :aria-label="$t('settings_shell.sim.day_label')">
          <button
            v-for="(label, i) in dayShort"
            :key="i"
            type="button"
            class="ss-dchip"
            :class="{ 'is-on': i === sim.day }"
            :aria-pressed="String(i === sim.day)"
            @click="sim.day = i"
          >
            {{ label }}
          </button>
        </div>
        <ui-segment
          :value="sim.side"
          :options="sides"
          :label="$t('settings_shell.sim.side_label')"
          @input="sim.side = $event"
        />
      </div>

      <p class="ss-sim__t">{{ ordersLine }}</p>
      <p v-if="!timeSet" class="ss-sim__t ss-sim__t--m">{{ $t("settings_shell.sim.no_time") }}</p>
      <p class="ss-sim__t ss-sim__t--r">
        <template v-if="days === 0">{{ $t("settings_shell.sim.today_only") }}</template>
        <template v-else>
          {{ $t("settings_shell.sim.reach") }} <b>{{ reachDate }}</b>{{ " " }}
          <span class="ss-sim__m">({{ $t("settings_shell.sim.ahead", { days: ctl.daysLabel(days) }) }})</span>
        </template>
      </p>

      <div class="ss-cal" role="img" :aria-label="`${ordersLine}. ${days === 0 ? $t('settings_shell.sim.today_only') : reachDate}`">
        <div class="ss-cal__h">
          <span v-for="(label, i) in dayShort" :key="i">{{ label }}</span>
        </div>
        <div class="ss-cal__g">
          <span v-for="cell in cells" :key="cell.offset" class="ss-cd" :class="cell.cls" :title="cell.title">
            <b>{{ cell.day }}</b>
            <i v-if="cell.month">{{ cell.month }}</i>
          </span>
        </div>
      </div>
      <div class="ss-cal__k">
        <span><i class="ss-k ss-k--order"></i>{{ $t("settings_shell.sim.k_order") }}</span>
        <span><i class="ss-k ss-k--ok"></i>{{ $t("settings_shell.sim.k_ok") }}</span>
        <span><i class="ss-k ss-k--last"></i>{{ $t("settings_shell.sim.k_last") }}</span>
      </div>
    </div>
  </set-card>
</template>

<script>
import { DAYS, okTime } from "../settingsLogic";
import SetCard from "./SetCard.vue";

const WEEKS = 3;

/**
 * "Šta radnik vidi": za izabrani dan u sedmici i trenutak (prije / poslije roka) pokazuje do kog dana radnik
 * može da bira obroke. Datumi su stvarni (sedmica koja počinje ponedjeljkom ove sedmice).
 */
export default {
  name: "SetSimulation",
  components: { SetCard },
  inject: { ctl: "settingsCtl" },
  props: {
    kind: { type: String, default: "days" },
  },
  computed: {
    sim() {
      return this.ctl.ui.sim[this.kind];
    },
    rule() {
      return this.ctl.form[this.kind][DAYS[this.sim.day]];
    },
    timeSet() {
      return okTime(this.rule.time);
    },
    days() {
      const value = this.sim.side === "before" ? this.rule.day_before_limit : this.rule.day_after_limit;
      const n = Number(value);
      return value === "" || value == null || Number.isNaN(n) ? 0 : n;
    },
    sides() {
      return [
        { value: "before", label: this.$t("settings_shell.sim.side_before") },
        { value: "after", label: this.$t("settings_shell.sim.side_after") },
      ];
    },
    weekStart() {
      const start = new Date();
      start.setHours(12, 0, 0, 0);
      start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
      return start;
    },
    dayShort() {
      return DAYS.map((_, i) => this.localeDate(this.dateAt(i), { weekday: "short" }));
    },
    last() {
      return this.sim.day + this.days;
    },
    ordersLine() {
      return this.$t("settings_shell.sim.orders", {
        day: this.$t(DAYS[this.sim.day]),
        side: this.sim.side === "before" ? this.$t("settings_shell.sim.side_before") : this.$t("settings_shell.sim.side_after"),
        time: this.rule.time || "—",
      });
    },
    reachDate() {
      return this.longDate(this.last);
    },
    cells() {
      const out = [];
      for (let offset = 0; offset < WEEKS * 7; offset++) {
        const date = this.dateAt(offset);
        const cls = [];
        if (offset < this.sim.day) {
          cls.push("is-past");
        } else if (offset === this.sim.day) {
          cls.push("is-order");
        }
        if (offset > this.sim.day && offset < this.last) {
          cls.push("is-ok");
        }
        if (offset === this.last) {
          cls.push("is-last");
        }
        if (offset > this.last) {
          cls.push("is-off");
        }
        out.push({
          offset,
          day: date.getDate(),
          month: offset === 0 || date.getDate() === 1 ? this.localeDate(date, { month: "short" }) : "",
          cls,
          title: this.longDate(offset),
        });
      }
      return out;
    },
  },
  methods: {
    dateAt(offset) {
      const date = new Date(this.weekStart);
      date.setDate(date.getDate() + offset);
      return date;
    },
    /** "Ponedjeljak, 2. novembar": naziv dana je iz prevoda aplikacije, datum prema jeziku. */
    longDate(offset) {
      const date = this.dateAt(offset);
      return `${this.$t(DAYS[(date.getDay() + 6) % 7])}, ${this.localeDate(date, { day: "numeric", month: "long" })}`;
    },
  },
};
</script>
